import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { SequencePreviewFrame } from "./SequencePreviewFrame";

const sanitizeEditableHtml = (html: string) =>
  DOMPurify.sanitize(html || "", {
    ADD_ATTR: ["target", "style"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|#|\/|{{)/i,
  });

interface VisualEmailEditorProps {
  value: string;
  onChange: (value: string) => void;
  subject?: string | null;
  type?: string;
}

export function VisualEmailEditor({ value, onChange, subject, type = "email" }: VisualEmailEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (!editorRef.current || lastValueRef.current === value) return;
    editorRef.current.innerHTML = sanitizeEditableHtml(value);
    lastValueRef.current = value;
  }, [value]);

  if (type !== "email") {
    return <SequencePreviewFrame html={value} subject={subject} type={type} />;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="border-b border-border bg-muted/40 px-4 py-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Visual email editor</div>
        <div className="mt-1 truncate text-sm font-semibold text-foreground">{subject || "No subject"}</div>
      </div>
      <div className="bg-muted/30 p-4">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="mx-auto max-w-[680px] overflow-hidden rounded-lg bg-background shadow-sm outline-none ring-2 ring-transparent transition focus:ring-primary/40 [&_*]:max-w-full"
          onInput={(event) => {
            const sanitized = sanitizeEditableHtml(event.currentTarget.innerHTML);
            lastValueRef.current = sanitized;
            onChange(sanitized);
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeEditableHtml(value) }}
        />
      </div>
    </div>
  );
}
